import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import MasterLayout from "../../../masterLayout/MasterLayout";
import Loader from "../../../components/Loader";
import { apiFetch } from "../../../utils/api";

const formatoFecha = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const PlatillosPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [datos, setDatos] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  const cargarDatos = async () => {
    setIsLoading(true);
    setError("");
    try {
      const data = await apiFetch("/api/products", { method: "GET" });
      if (data.status) {
        setDatos(Array.isArray(data.data) ? data.data : []);
      } else {
        setDatos([]);
        setError(data.message || "No se pudo cargar merch.");
      }
    } catch (error) {
      console.error("Error:", error);
      setDatos([]);
      setError(error.message || "No se pudo cargar merch.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, []);

  const filteredData = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return datos;
    return datos.filter((item) => {
      const values = [
        item.nombre,
        item.descripcion,
        item.categoria?.nombre,
        item.tienda?.branch?.nombre,
      ].filter(Boolean).join(" ").toLowerCase();
      return values.includes(term);
    });
  }, [datos, search]);

  const allVisibleSelected = filteredData.length > 0 && filteredData.every((item) => selectedIds.includes(item.id));

  const toggleItem = (id) => {
    setSelectedIds((current) => current.includes(id)
      ? current.filter((item) => item !== id)
      : [...current, id]);
  };

  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((current) => current.filter((id) => !filteredData.some((item) => item.id === id)));
      return;
    }
    setSelectedIds((current) => Array.from(new Set([...current, ...filteredData.map((item) => item.id)])));
  };

  const eliminarSeleccionados = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`¿Eliminar ${selectedIds.length} productos seleccionados?`)) return;

    setIsDeleting(true);
    try {
      const data = await apiFetch("/api/products/bulk-delete", {
        method: "POST",
        body: JSON.stringify({ ids: selectedIds }),
      });

      if (!data.status) {
        throw new Error(data.message || "No se pudieron eliminar los productos.");
      }

      setSelectedIds([]);
      await cargarDatos();
    } catch (error) {
      console.error("Error:", error);
      setError(error.message || "No se pudieron eliminar los productos.");
    } finally {
      setIsDeleting(false);
    }
  };

  const eliminarItem = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    setIsDeleting(true);
    try {
      const data = await apiFetch(`/api/products/${id}`, { method: "DELETE" });
      if (!data.status) {
        throw new Error(data.message || "No se pudo eliminar el producto.");
      }
      setSelectedIds((current) => current.filter((item) => item !== id));
      await cargarDatos();
    } catch (error) {
      console.error("Error:", error);
      setError(error.message || "No se pudo eliminar el producto.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <MasterLayout>
      {isLoading ? (
        <Loader />
      ) : (
        <div className="card basic-data-table">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h5 className="card-title mb-1">Merch</h5>
              <p className="text-muted mb-0">Selecciona varios productos para eliminarlos en una sola acción.</p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-outline-danger"
                disabled={selectedIds.length === 0 || isDeleting}
                onClick={eliminarSeleccionados}
              >
                Eliminar seleccionados ({selectedIds.length})
              </button>
              <Link to="/merch/0" className="btn btn-success">Agregar</Link>
            </div>
          </div>

          <div className="card-body">
            {error && <div className="alert alert-warning">{error}</div>}
            <div className="d-flex justify-content-end mb-3">
              <input
                className="form-control"
                style={{ maxWidth: 320 }}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar merch"
              />
            </div>

            <div className="table-responsive">
              <table className="table bordered-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 48 }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={toggleAllVisible}
                      />
                    </th>
                    <th>NOMBRE</th>
                    <th>DESCRIPCIÓN</th>
                    <th>CATEGORÍA</th>
                    <th>SUCURSAL</th>
                    <th>PRECIO</th>
                    <th>FECHA ALTA</th>
                    <th>ACCIONES</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center">No hay productos disponibles.</td>
                    </tr>
                  ) : filteredData.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={selectedIds.includes(item.id)}
                          onChange={() => toggleItem(item.id)}
                        />
                      </td>
                      <td>{item.nombre}</td>
                      <td>{item.descripcion}</td>
                      <td>{item.categoria?.nombre || ""}</td>
                      <td>{item.tienda?.branch?.nombre || ""}</td>
                      <td>${Number(item.precio || 0).toFixed(2)}</td>
                      <td>{formatoFecha(item.createdAt)}</td>
                      <td>
                        <div className="d-flex">
                          <Link to={`/merch/${item.id}`} className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center">
                            <i className="ri-edit-line" style={{ fontSize: 16 }}></i>
                          </Link>
                          <button
                            type="button"
                            className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                            onClick={() => eliminarItem(item.id)}
                            disabled={isDeleting}
                          >
                            <i className="ri-delete-bin-2-line" style={{ fontSize: 16 }}></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </MasterLayout>
  );
};

export default PlatillosPage;
